<template>
    <v-dialog v-model="internalValue" max-width="1400px" @click:outside="internalValue = false">
        <v-card class="rounded-card">
            <v-card-title class="headline" style="background-color: #009f89; color: #ffffff">
                <span style="display: flex; align-items: center">
                    <span v-if="getPlatformInfo(item.platform).logo" v-html="getPlatformInfo(item.platform).logo" style="width: 28px; height: 28px; margin-right: 8px"></span>
                    <span>{{ getPlatformInfo(item.platform).label || item.platform }}</span>
                </span>
                <v-spacer />
                <!-- Datumsauswahl oben -->

                <v-select
                    v-if="dateOptions.length"
                    class="cd-date-select mr-2"
                    :items="dateOptions"
                    item-text="label"
                    item-value="value"
                    v-model="selectedDate"
                    label="Date"
                    dense
                    color="white"
                    filled
                    hide-details
                    rounded
                    :menu-props="{ attach: 'body', eager: true, offsetY: true, maxHeight: 320, contentClass: 'cd-date-menu' }" />
                <v-btn color="white" icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-subtitle style="background-color: #009f89; color: #ffffff" class="cd-subheader">
                <div class="mt-2">
                    Last update:
                    <v-chip class="ml-1 px-2" x-small color="white" outlined>
                        {{ formatDateOnly(selectedDate || item.importDate) }}
                    </v-chip>
                </div>
            </v-card-subtitle>

            <v-card-text class="cd-dialog-content pr-0 pb-0">
                <div class="cd-chat-area">
                    <div class="cd-message-bubble cd-user-message">
                        <div class="cd-comment-text" v-html="highlightedPrompt" :key="activeTypes.join(',') + '-prompt'"></div>
                    </div>
                    <div class="cd-message-bubble cd-ki-message">
                        <div class="cd-comment-text" v-html="highlightedResponse" :key="activeTypes.join(',') + '-response'"></div>
                    </div>
                </div>
                <div class="cd-sidebar">
                    <div class="cd-sidebar-title">Entity-Types</div>
                    <v-chip-group v-model="activeTypes" multiple column class="cd-chip-group my-2">
                        <v-chip v-for="type in entityTypes" :key="type" :value="type" :color="entityTypeColor(type)" filter outlined>
                            {{ type }}
                        </v-chip>
                    </v-chip-group>

                    <!-- NEU: Top Entities (aus item.mergedEntities) -->
                    <v-divider class="my-3"></v-divider>
                    <div class="cd-sidebar-title" style="margin-top: 4px; display: flex; align-items: center">
                        <span>Top Entities</span>
                        <v-tooltip top max-width="320">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey">mdi-help-circle-outline</v-icon>
                            </template>
                            <div style="font-size: 12px; line-height: 1.25">
                                <b>Top Entities</b>
                                <ul style="margin: 6px 0 0 16px; padding: 0">
                                    <li>Top 10 entities ranked by salience from the current response.</li>
                                    <li>Respects the selected Entity-Types filter above.</li>
                                    <li>Displays name, type, and salience (as % if ≤ 1.0).</li>
                                </ul>
                            </div>
                        </v-tooltip>
                    </div>
                    <div class="cd-top-entities">
                        <div v-for="(e, i) in bestEntities" :key="e.name + '-' + i" class="cd-entity-row">
                            <span class="cd-entity-name">{{ e.name }}</span>
                            <!-- <v-chip x-small class="ml-2" :color="entityTypeColor(e.type)" outlined>{{ e.type }}</v-chip> -->
                            <span class="cd-entity-salience">{{ formatSalience(e.salience) }}</span>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import MarkdownIt from "markdown-it"
    export default {
        props: {
            value: { type: Boolean, required: true },
            item: { type: Object, required: true },
            // NEU: verfügbare Tage (ISO/Date-Strings)
            availableDates: { type: Array, default: () => [] }
        },
        data() {
            return {
                internalValue: this.value,
                activeTypes: [],
                md: new MarkdownIt(),
                entitiesLocal: [],
                // NEU: ausgewähltes Datum
                selectedDate: this.item?.importDate ? new Date(this.item.importDate).toISOString().slice(0, 10) : null
            }
        },
        computed: {
            // NEU: Optionen fürs Select (formatiert)
            dateOptions() {
                const raw = Array.isArray(this.availableDates) && this.availableDates.length ? this.availableDates : this.item?.importDate ? [this.item.importDate] : []
                return raw
                    .filter(Boolean)
                    .map((d) => ({ label: this.formatDateOnly(d), value: this.toDay(d) })) // <-- fix
                    .sort((a, b) => new Date(b.value) - new Date(a.value))
            },
            entityTypes() {
                if (!Array.isArray(this.entitiesLocal)) return []
                return [...new Set(this.entitiesLocal.map((e) => e.type))]
            },
            highlightedPrompt() {
                void this.activeTypes.join(",")
                return this.highlightEntities(this.item.prompt, "prompt")
            },
            highlightedResponse() {
                void this.activeTypes.join(",")
                return this.highlightEntities(this.item.responseText, "responseText")
            },
            bestEntities() {
                const source = Array.isArray(this.item?.mergedEntities) && this.item.mergedEntities.length ? this.item.mergedEntities : Array.isArray(this.entitiesLocal) ? this.entitiesLocal : []
                const filtered = this.activeTypes?.length ? source.filter((e) => this.activeTypes.includes(e.type)) : source
                return filtered
                    .filter((e) => e && e.name)
                    .map((e) => ({ name: e.name, type: e.type || "OTHER", salience: Number(e.salience) || 0 }))
                    .sort((a, b) => b.salience - a.salience)
                    .slice(0, 10)
            }
        },
        watch: {
            value(val) {
                this.internalValue = val
                if (val) {
                    this.activeTypes = []
                    // beim Öffnen Datum auf aktuelles Item setzen
                    this.selectedDate = this.toDay(this.item?.importDate) || (this.availableDates[0] ? this.toDay(this.availableDates[0]) : null) // <-- fix
                }
            },
            internalValue(val) {
                this.$emit("input", val)
            },
            // bei Item-Wechsel Datum nachziehen
            "item.importDate"(nv) {
                if (nv && this.selectedDate !== this.toDay(nv)) this.selectedDate = this.toDay(nv) // <-- fix
            },
            // NEU: beim Datumswechsel Parent informieren
            selectedDate(nv, ov) {
                if (!nv || nv === ov) return
                // Parent lädt den passenden Response und setzt 'item' neu
                this.$emit("change-date", nv)
            },
            "item._id": {
                immediate: true,
                handler(newVal) {
                    if (newVal) {
                        this.fetchEntities(newVal).then((entities) => {
                            this.entitiesLocal = Array.isArray(entities) ? entities : []
                            this.activeTypes = [] //"OTHER", "ORGANIZATION"
                        })
                    }
                }
            }
        },
        mounted() {
            if (this.item && this.item._id) {
                this.fetchEntities(this.item._id).then((entities) => {
                    console.log("Fetched entities:", entities)
                    this.entitiesLocal = Array.isArray(entities) ? entities : []
                    this.activeTypes = []
                })
            }
        },
        methods: {
            fetchEntities(response_id) {
                return new Promise(async (resolve, reject) => {
                    try {
                        const url =
                            (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                                this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                            ) == -1
                                ? process.env.VUE_APP_MONITORING_API_URL
                                : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/response/entities"
                        let post_data = {
                            company_id: localStorage.company_id,
                            response_id: response_id
                        }
                        let response_data = await this.$helpers.axiosPost(url, post_data)
                        resolve(response_data.data.entities.entities)
                    } catch (error) {
                        console.log(error)
                        reject(error)
                    }
                })
            },
            formatDateOnly(date) {
                if (!date) return ""
                return new Date(date).toLocaleDateString()
            },
            close() {
                this.internalValue = false
            },
            entityTypeColor(type) {
                const colors = {
                    ORGANIZATION: "blue",
                    PERSON: "success",
                    LOCATION: "info",
                    PRODUCT: "warning",
                    EVENT: "error",
                    OTHER: "grey",
                    CONSUMER_GOOD: "#fbc02d"
                }
                return colors[type] || "grey"
            },
            getPlatformInfo(name) {
                // Name normalisieren (z.B. für "openai" → "openai")
                const key = (name || "").toString().trim().toLowerCase()
                const platforms = {
                    chatgpt: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.639 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "openai",
                        status: "active"
                    },
                    google: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AIO",
                        status: "active"
                    },
                    gemini: {
                        logo: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65 65"><mask id="maskme" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="65" height="65"><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="#000"/><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="url(#prefix__paint0_linear_2001_67)"/></mask><g mask="url(#maskme)"><g filter="url(#prefix__filter0_f_2001_67)"><path d="M-5.859 50.734c7.498 2.663 16.116-2.33 19.249-11.152 3.133-8.821-.406-18.131-7.904-20.794-7.498-2.663-16.116 2.33-19.25 11.151-3.132 8.822.407 18.132 7.905 20.795z" fill="#FFE432"/></g><g filter="url(#prefix__filter1_f_2001_67)"><path d="M27.433 21.649c10.3 0 18.651-8.535 18.651-19.062 0-10.528-8.35-19.062-18.651-19.062S8.78-7.94 8.78 2.587c0 10.527 8.35 19.062 18.652 19.062z" fill="#FC413D"/></g><g filter="url(#prefix__filter2_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter3_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter4_f_2001_67)"><path d="M30.954 74.181c9.014-5.485 11.427-17.976 5.389-27.9-6.038-9.925-18.241-13.524-27.256-8.04-9.015 5.486-11.428 17.977-5.39 27.902 6.04 9.924 18.242 13.523 27.257 8.038z" fill="#00B95C"/></g><g filter="url(#prefix__filter5_f_2001_67)"><path d="M67.391 42.993c10.132 0 18.346-7.91 18.346-17.666 0-9.757-8.214-17.667-18.346-17.667s-18.346 7.91-18.346 17.667c0 9.757 8.214 17.666 18.346 17.666z" fill="#3186FF"/></g><g filter="url(#prefix__filter6_f_2001_67)"><path d="M-13.065 40.944c9.33 7.094 22.959 4.869 30.442-4.972 7.483-9.84 5.987-23.569-3.343-30.663C4.704-1.786-8.924.439-16.408 10.28c-7.483 9.84-5.986 23.57 3.343 30.664z" fill="#FBBC04"/></g><g filter="url(#prefix__filter7_f_2001_67)"><path d="M34.74 51.43c11.135 7.656 25.896 5.524 32.968-4.764 7.073-10.287 3.779-24.832-7.357-32.488C49.215 6.52 34.455 8.654 27.382 18.94c-7.072 10.288-3.779 24.833 7.357 32.49z" fill="#3186FF"/></g><g filter="url(#prefix__filter8_f_2001_67)"><path d="M54.984-2.336c2.833 3.852-.808 11.34-8.131 16.727-7.324 5.387-15.557 6.631-18.39 2.78-2.833-3.853.807-11.342 8.13-16.728 7.324-5.387 15.558-6.631 18.39-2.78z" fill="#749BFF"/></g><g filter="url(#prefix__filter9_f_2001_67)"><path d="M31.727 16.104C43.053 5.598 46.94-8.626 40.41-15.666c-6.53-7.04-21.006-4.232-32.332 6.274s-15.214 24.73-8.683 31.77c6.53 7.04 21.006 4.232 32.332-6.274z" fill="#FC413D"/></g><g filter="url(#prefix__filter10_f_2001_67)"><path d="M8.51 53.838c6.732 4.818 14.46 5.55 17.262 1.636 2.802-3.915-.384-10.994-7.116-15.812-6.731-4.818-14.46-5.55-17.261-1.636-2.802 3.915.383 10.994 7.115 15.812z" fill="#FFEE48"/></g></g><defs><filter id="prefix__filter0_f_2001_67" x="-19.824" y="13.152" width="39.274" height="43.217" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="2.46" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter1_f_2001_67" x="-15.001" y="-40.257" width="84.868" height="85.688" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="11.891" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter2_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter3_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter4_f_2001_67" x="-19.845" y="15.459" width="79.731" height="81.505" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter5_f_2001_67" x="29.832" y="-11.552" width="75.117" height="73.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="9.606" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter6_f_2001_67" x="-38.583" y="-16.253" width="78.135" height="78.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="8.706" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter7_f_2001_67" x="8.107" y="-5.966" width="78.877" height="77.539" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.775" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter8_f_2001_67" x="13.587" y="-18.488" width="56.272" height="51.81" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="6.957" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter9_f_2001_67" x="-15.526" y="-31.297" width="70.856" height="69.306" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="5.876" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter10_f_2001_67" x="-14.168" y="20.964" width="55.501" height="51.571" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.273" result="effect1_foregroundBlur_2001_67"/></filter><linearGradient id="prefix__paint0_linear_2001_67" x1="18.447" y1="43.42" x2="52.153" y2="15.004" gradientUnits="userSpaceOnUse"><stop stop-color="#4893FC"/><stop offset=".27" stop-color="#4893FC"/><stop offset=".777" stop-color="#969DFF"/><stop offset="1" stop-color="#BD99FE"/></linearGradient></defs></svg>`,
                        label: "Gemini",
                        status: "coming soon"
                    },
                    claude: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#D77655" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.612-115.613 115.612H115.612C52.026 509.639 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill="#FCF2EE" fill-rule="nonzero" d="M142.27 316.619l73.655-41.326 1.238-3.589-1.238-1.996-3.589-.001-12.31-.759-42.084-1.138-36.498-1.516-35.361-1.896-8.897-1.895-8.34-10.995.859-5.484 7.482-5.03 10.717.935 23.683 1.617 35.537 2.452 25.782 1.517 38.193 3.968h6.064l.86-2.451-2.073-1.517-1.618-1.517-36.776-24.922-39.81-26.338-20.852-15.166-11.273-7.683-5.687-7.204-2.451-15.721 10.237-11.273 13.75.935 3.513.936 13.928 10.716 29.749 23.027 38.848 28.612 5.687 4.727 2.275-1.617.278-1.138-2.553-4.271-21.13-38.193-22.546-38.848-10.035-16.101-2.654-9.655c-.935-3.968-1.617-7.304-1.617-11.374l11.652-15.823 6.445-2.073 15.545 2.073 6.547 5.687 9.655 22.092 15.646 34.78 24.265 47.291 7.103 14.028 3.791 12.992 1.416 3.968 2.449-.001v-2.275l1.997-26.641 3.69-32.707 3.589-42.084 1.239-11.854 5.863-14.206 11.652-7.683 9.099 4.348 7.482 10.716-1.036 6.926-4.449 28.915-8.72 45.294-5.687 30.331h3.313l3.792-3.791 15.342-20.372 25.782-32.227 11.374-12.789 13.27-14.129 8.517-6.724 16.1-.001 11.854 17.617-5.307 18.199-16.581 21.029-13.75 17.819-19.716 26.54-12.309 21.231 1.138 1.694 2.932-.278 44.536-9.479 24.062-4.347 28.714-4.928 12.992 6.066 1.416 6.167-5.106 12.613-30.71 7.583-36.018 7.204-53.636 12.689-.657.48.758.935 24.164 2.275 10.337.556h25.301l47.114 3.514 12.309 8.139 7.381 9.959-1.238 7.583-18.957 9.655-25.579-6.066-59.702-14.205-20.474-5.106-2.83-.001v1.694l17.061 16.682 31.266 28.233 39.152 36.397 1.997 8.999-5.03 7.102-5.307-.758-34.401-25.883-13.27-11.651-30.053-25.302-1.996-.001v2.654l6.926 10.136 36.574 54.975 1.895 16.859-2.653 5.485-9.479 3.311-10.414-1.895-21.408-30.054-22.092-33.844-17.819-30.331-2.173 1.238-10.515 113.261-4.929 5.788-11.374 4.348-9.478-7.204-5.03-11.652 5.03-23.027 6.066-30.052 4.928-23.886 4.449-29.674 2.654-9.858-.177-.657-2.173.278-22.37 30.71-34.021 45.977-26.919 28.815-6.445 2.553-11.173-5.789 1.037-10.337 6.243-9.2 37.257-47.392 22.47-29.371 14.508-16.961-.101-2.451h-.859l-98.954 64.251-17.618 2.275-7.583-7.103.936-11.652 3.589-3.791 29.749-20.474-.101.102.024.101z"/></svg>`,
                        label: "Claude",
                        status: "coming soon"
                    },
                    perplexity: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#1F1F1F" d="M115.613 0h280.774C459.974 0 512 52.025 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.613C52.026 509.64 0 457.614 0 394.027V115.612C0 52.025 52.026 0 115.613 0z"/><path fill="#fff" fill-rule="nonzero" d="M348.851 128.063l-68.946 58.302h68.946v-58.302zm-83.908 48.709l100.931-85.349v94.942h32.244v143.421h-38.731v90.004l-94.442-86.662v83.946h-17.023v-83.906l-96.596 86.246v-89.628h-37.445V186.365h38.732V90.768l95.309 84.958v-83.16h17.023l-.002 84.206zm-29.209 26.616c-34.955.02-69.893 0-104.83 0v109.375h20.415v-27.121l84.415-82.254zm41.445 0l82.208 82.324v27.051h21.708V203.388c-34.617 0-69.274.02-103.916 0zm-42.874-17.023l-64.669-57.646v57.646h64.669zm13.617 124.076v-95.2l-79.573 77.516v88.731l79.573-71.047zm17.252-95.022v94.863l77.19 70.83c0-29.485-.012-58.943-.012-88.425l-77.178-77.268z"/></svg>`,
                        label: "Perplexity",
                        status: "coming soon"
                    },
                    grok: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" ><path d="M18.542 30.532l15.956-11.776c.783-.576 1.902-.354 2.274.545 1.962 4.728 1.084 10.411-2.819 14.315-3.903 3.901-9.333 4.756-14.299 2.808l-5.423 2.511c7.778 5.315 17.224 4 23.125-1.903 4.682-4.679 6.131-11.058 4.775-16.812l.011.011c-1.966-8.452.482-11.829 5.501-18.735C47.759 1.332 47.88 1.166 48 1l-6.602 6.599V7.577l-22.86 22.958M15.248 33.392c-5.582-5.329-4.619-13.579.142-18.339 3.521-3.522 9.294-4.958 14.331-2.847l5.412-2.497c-.974-.704-2.224-1.46-3.659-1.994-6.478-2.666-14.238-1.34-19.505 3.922C6.904 16.701 5.31 24.488 8.045 31.133c2.044 4.965-1.307 8.48-4.682 12.023C2.164 44.411.967 45.67 0 47l15.241-13.608"/></svg>`,
                        label: "Grok",
                        status: ""
                    },
                    google_ai_mode: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AI Mode"
                    },
                    microsoft_copilot: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48"><path d="M34.142 7.325A4.63 4.63 0 0029.7 4H28.35a4.63 4.63 0 00-4.554 3.794L21.48 20.407l.575-1.965a4.63 4.63 0 014.444-3.33h7.853l3.294 1.282 3.175-1.283h-.926a4.63 4.63 0 01-4.443-3.325l-1.31-4.461z" fill="url(#prefix__paint0_radial_56201_15503)"/><path d="M14.33 40.656A4.63 4.63 0 0018.779 44h2.87a4.63 4.63 0 004.629-4.51l.312-12.163-.654 2.233a4.63 4.63 0 01-4.443 3.329h-7.919l-2.823-1.532-3.057 1.532h.912a4.63 4.63 0 014.447 3.344l1.279 4.423z" fill="url(#prefix__paint1_radial_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint2_linear_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint3_linear_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint4_radial_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint5_linear_56201_15503)"/><defs><radialGradient id="prefix__paint0_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-10.96051 -13.38922 12.59013 -10.30637 38.005 20.514)"><stop offset=".096" stop-color="#00AEFF"/><stop offset=".773" stop-color="#2253CE"/><stop offset="1" stop-color="#0736C4"/></radialGradient><radialGradient id="prefix__paint1_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(51.84 -28.201 27.85) scale(15.9912 15.5119)"><stop stop-color="#FFB657"/><stop offset=".634" stop-color="#FF5F3D"/><stop offset=".923" stop-color="#C02B3C"/></radialGradient><radialGradient id="prefix__paint4_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(109.274 16.301 20.802) scale(38.3873 45.9867)"><stop offset=".066" stop-color="#8C48FF"/><stop offset=".5" stop-color="#F2598A"/><stop offset=".896" stop-color="#FFB152"/></radialGradient><linearGradient id="prefix__paint2_linear_56201_15503" x1="12.5" y1="7.5" x2="14.788" y2="33.975" gradientUnits="userSpaceOnUse"><stop offset=".156" stop-color="#0D91E1"/><stop offset=".487" stop-color="#52B471"/><stop offset=".652" stop-color="#98BD42"/><stop offset=".937" stop-color="#FFC800"/></linearGradient><linearGradient id="prefix__paint3_linear_56201_15503" x1="14.5" y1="4" x2="15.75" y2="32.885" gradientUnits="userSpaceOnUse"><stop stop-color="#3DCBFF"/><stop offset=".247" stop-color="#0588F7" stop-opacity="0"/></linearGradient><linearGradient id="prefix__paint5_linear_56201_15503" x1="42.586" y1="13.346" x2="42.569" y2="21.215" gradientUnits="userSpaceOnUse"><stop offset=".058" stop-color="#F8ADFA"/><stop offset=".708" stop-color="#A86EDD" stop-opacity="0"/></linearGradient></defs></svg>`,
                        label: "Microsoft Copilot"
                    }
                }
                return platforms[key] || { logo: null, label: name, status: null }
            },
            highlightEntities(text, field) {
                if (!text || !Array.isArray(this.entitiesLocal)) return text
                let html = this.md.render(text)
                this.entitiesLocal
                    .filter((e) => this.activeTypes.includes(e.type))
                    .forEach((entity) => {
                        if (!entity.name) return
                        const safeName = entity.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                        const regex = new RegExp(`(>[^<]*)\\b(${safeName})\\b`, "gi")
                        const typeClass = entity.type.toLowerCase().replace(/ /g, "_")
                        html = html.replace(regex, (match, prefix, name) => `${prefix}<span class="cd-entity-highlight entity-${typeClass}">${name}</span>`)
                    })
                if (field == "responseText" && this.item.platform == "gemini") {
                    html += this.item.sources.map((source) => (source.url ? `<span><a href="${source.url}" target="_blank" rel="noopener noreferrer">${source.url}</a></span>` : "")).join("")
                }
                html = this.replaceSourcesWithChips(html)
                return html
            },
            replaceSourcesWithChips(html) {
                if (!html) return html

                const isDomainOrUrl = (text) => /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}([\/?#]\S*)?$/i.test((text || "").trim())

                const normalizeUrl = (raw) => {
                    const t = (raw || "").trim()
                    if (!isDomainOrUrl(t)) return null
                    const href = /^https?:\/\//i.test(t) ? t : `https://${t}`
                    try {
                        const u = new URL(href)
                        return { href: u.href, domain: u.hostname }
                    } catch {
                        return null
                    }
                }

                const esc = (s = "") => s.replace(/["<>]/g, (c) => ({ '"': "&quot;", "<": "&lt;", ">": "&gt;" }[c]))

                // Chip als echter Link: öffnet nur die exakte URL (href)
                const makeChip = ({ href, domain }) => {
                    const safeHref = esc(href)
                    const safeDomain = esc(domain)
                    const fav = `https://s2.googleusercontent.com/s2/favicons?domain=${encodeURIComponent(safeDomain)}`
                    return `
                <a class="cd-source-chip" href="${safeHref}" target="_blank" rel="noopener noreferrer" title="${safeHref}">
                    <img class="fav" src="${fav}" alt="${safeDomain}" loading="lazy" referrerpolicy="no-referrer" onerror="this.style.display='none'">
                    <span class="srclabel">${safeDomain}</span>
                </a>`
                }

                // [[[...]]] oder [[...]]
                html = html.replace(/\[{2,3}\s*([^[\]]+?)\s*\]{2,3}/g, (m, raw) => {
                    const norm = normalizeUrl(raw)
                    return norm ? makeChip(norm) : m
                })

                // Vorhandene Links: immer href verwenden (exakte URL), Text ignorieren
                html = html.replace(/<a href="([^"]+)"[^>]*>[\s\S]*?<\/a>/gi, (m, url) => {
                    const normUrl = normalizeUrl(url) || normalizeUrl(`https://${url}`)
                    return normUrl ? makeChip(normUrl) : m
                })

                return html
            },
            toDay(d) {
                // <-- add
                if (!d) return null
                if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d
                const dt = new Date(d)
                return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
            },
            formatSalience(v) {
                const n = Number(v)
                if (!isFinite(n)) return ""
                // falls Salience 0..1 ist → Prozent, sonst roh mit 2 Nachkommastellen
                if (n <= 1) return `${(n * 100).toFixed(1)}%`
                return n.toFixed(2)
            }
        }
    }
</script>

<style>
    /* Chat-Bubble-Optik für chatDialog.vue */
    .cd-message-bubble {
        display: flex;
        flex-direction: column;
        margin-bottom: 18px;
        max-width: 90%;
        min-width: 120px;
        border-radius: 20px;
        background: none;

        padding: 0;
    }

    .cd-user-message {
        align-items: flex-end;
        margin-left: auto;
    }
    .cd-user-message .cd-comment-text {
        background-color: #e9e9e980;
        color: #0d0d0d;
        border-radius: 16px 16px 4px 16px;
        margin-left: auto;
        padding: 10px 14px;
        font-size: 1rem;

        word-break: break-word;
        max-width: 100%;
    }

    .cd-ki-message {
        align-items: flex-start;
        margin-right: auto;
    }
    .cd-ki-message .cd-comment-text {
        /* background-color: #f7fafc; */
        color: #0d0d0d;
        border-radius: 16px 16px 16px 16px;

        padding: 10px 14px;
        font-size: 1rem;
        word-break: break-word;
        max-width: 100%;
    }

    .cd-comment-user {
        margin-left: 5px;
        font-weight: bold;
        font-size: 12px;
        padding-bottom: 6px;
        color: #1976d2;
        letter-spacing: 0.5px;
    }

    .cd-entity-highlight {
        color: var(--v-primary-base);
        border: 1.5px solid var(--v-primary-base);
        border-radius: 4px;
        padding: 1px 4px;
        background: rgba(44, 123, 229, 0.07); /* leichtes Primary-Highlight */
        font-weight: 600;
        margin: 0 1px;
        box-shadow: none;
    }

    .cd-entity-highlight.entity-organization {
        color: #1976d2;
        border-color: #1976d2;
        background: rgba(25, 118, 210, 0.07);
    }
    .cd-entity-highlight.entity-person {
        color: #43a047;
        border-color: #43a047;
        background: rgba(67, 160, 71, 0.07);
    }
    .cd-entity-highlight.entity-location {
        color: #0288d1;
        border-color: #0288d1;
        background: rgba(2, 136, 209, 0.07);
    }
    .cd-entity-highlight.entity-product {
        color: #ffa000;
        border-color: #ffa000;
        background: rgba(255, 160, 0, 0.07);
    }
    .cd-entity-highlight.entity-event {
        color: #e53935;
        border-color: #e53935;
        background: rgba(229, 57, 53, 0.07);
    }
    .cd-entity-highlight.entity-other {
        color: #757575;
        border-color: #757575;
        background: rgba(117, 117, 117, 0.07);
    }
    .cd-entity-highlight.entity-consumer_good {
        color: #fbc02d;
        border-color: #fbc02d;
        background: rgba(251, 192, 45, 0.07);
    }

    .cd-chip-group {
        margin-top: 10px;
        margin-bottom: 0;
    }
    .cd-comment-text table {
        width: 100%;
        border-collapse: collapse;
        margin: 8px 0;
        display: block; /* ermöglicht horizontales Scrollen */
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .cd-comment-text th,
    .cd-comment-text td {
        padding: 8px 10px;
        border: 1px solid #e0e0e0;
        text-align: left;
        vertical-align: top;
        background: #fff;
    }

    .cd-comment-text thead th {
        background: #f5f5f7;
        font-weight: 600;
    }

    .cd-comment-text tbody tr:nth-child(odd) td {
        background: #fafbfc;
    }

    /* Optional: kleinere Typo auf Mobile */
    @media (max-width: 600px) {
        .cd-comment-text th,
        .cd-comment-text td {
            padding: 6px 8px;
            font-size: 0.95em;
        }
    }
    @media (max-width: 600px) {
        .cd-message-bubble {
            max-width: 100%;
            min-width: 0;
            padding: 0;
        }
        .cd-user-message .cd-comment-text,
        .cd-ki-message .cd-comment-text {
            font-size: 0.95rem;
            padding: 8px 8px;
        }
    }
    .cd-comment-text p {
        padding: 0.3rem 0;
    }
    .cd-comment-text p {
        margin-bottom: 5px;
    }
    .cd-comment-text hr {
        margin-bottom: 5px;
        margin-top: 5px;
    }
    .cd-source-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 2px 8px;
        border-radius: 16px;
        border: 1px solid #1976d2;
        color: #1976d2;
        background: #fff;
        cursor: pointer;
        font-size: 0.85em;
        line-height: 1;
        margin: 0 2px;
        vertical-align: middle;
    }
    .cd-comment-text a,
    .cd-comment-text a:visited {
        text-decoration: none;
        border-bottom: 0;
    }

    /* Stelle die untere Border für den Chip wieder her */
    .cd-comment-text a.cd-source-chip,
    .cd-comment-text a.cd-source-chip:visited {
        border-bottom-width: 1px;
        border-bottom-style: solid; /* Farbe kommt aus .cd-source-chip (und :hover) */
    }
    .cd-source-chip:hover {
        background: #eaf3ff;
        border-color: #1565c0;
    }
    .cd-source-chip .fav {
        margin: 0px;
        width: 16px;
        height: 16px;
        border-radius: 3px;
        object-fit: contain;
    }
    .cd-source-chip .srclabel {
        white-space: nowrap;
        max-width: 26ch;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .cd-comment-text h1 {
        padding: 1.5rem 0 0.8rem 0;
    }
    .cd-comment-text h2 {
        padding: 1.25rem 0 0.6rem 0;
    }
    .cd-comment-text h3 {
        padding: 0.8rem 0 0.2rem 0;
    }
    .cd-comment-text h4,
    .cd-comment-text h5,
    .cd-comment-text h6 {
        padding: 0.75rem 0 0.25rem 0;
    }

    /* Listen */
    .cd-comment-text ul,
    .cd-comment-text ol {
        padding: 0.5rem 1.25rem;
        margin: 0;
    }
    .cd-comment-text li {
        padding: 0.25rem 0;
    }

    .cd-dialog-content {
        display: flex;
        flex-direction: row;
        gap: 0;
        min-height: 350px;
        padding: 0;
    }

    .cd-chat-area {
        flex: 1 1 0;
        padding: 32px 32px 32px 32px;
        min-width: 0;
        max-height: 60vh;
        overflow-y: auto;
        background: none;
        display: flex;
        flex-direction: column;
    }

    .cd-sidebar {
        width: 320px;
        background: #f4f4f7;
        border-left: 1px solid #e0e0e0;
        padding: 32px 24px;
        display: flex;
        flex-direction: column;
        min-height: 100%;
        box-sizing: border-box;
    }

    .cd-sidebar-title {
        font-weight: bold;
        font-size: 1.1rem;
        margin-bottom: 16px;
        color: #555;
    }

    @media (max-width: 900px) {
        .cd-dialog-content {
            flex-direction: column;
        }
        .cd-sidebar {
            width: 100%;
            border-left: none;
            border-top: 1px solid #e0e0e0;
            padding: 16px 12px;
            min-height: unset;
        }
        .cd-chat-area {
            padding: 24px 8px 8px 8px;
            max-height: 40vh;
        }
    }

    /* Bilder im Chat-Inhalt responsiv */
    .cd-comment-text img {
        display: inline;
        max-height: 150px;
        height: auto;
        width: auto;
        border-radius: 8px;
        margin: 8px 5px;
        object-fit: contain;
    }

    /* Optional: sehr große Bilder begrenzen */
    .cd-comment-text img.large,
    .cd-comment-text figure img {
        max-height: 20vh;
    }

    /* Markdown-Figuren */
    .cd-comment-text figure {
        margin: 8px 0;
    }
    .cd-comment-text figcaption {
        font-size: 0.85rem;
        color: #666;
        text-align: center;
        margin-top: 4px;
    }

    /* Optional: eingebettete Medien responsiv */
    .cd-comment-text iframe,
    .cd-comment-text video {
        max-width: 100%;
        width: 100%;
        height: auto;
        aspect-ratio: 16 / 9;
    }

    /* Untertitel-Layout mit Select rechts */
    .cd-subheader {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    /* Select auf dunklem/gesättigtem Header besser sichtbar machen */
    .cd-date-select > .v-input__control > .v-input__slot {
        background: rgba(255, 255, 255, 0.14) !important;
        border-color: rgba(255, 255, 255, 0.5) !important;
        color: #fff !important;
    }
    .cd-date-select .v-label {
        color: #fff !important;
    }
    .cd-date-select .v-select__selections {
        color: #fff !important;
    }

    .cd-top-entities .cd-entity-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
        padding: 4px 0;
        font-size: 0.92rem;
    }
    .cd-top-entities .cd-entity-name {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .cd-top-entities .cd-entity-salience {
        color: #666;
        font-size: 0.85rem;
        margin-left: 6px;
    }
</style>
